import { useMemo } from "react";
import { useBlobField } from "./BlobFieldContext";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function BlobFieldViewer() {
    const {
        viewBoxAttr,
        canvasW,
        canvasH,
        gridPoints,
        contourData,
        contourLevels,
        fillColor,
        strokeColor,
        showGridPoints,
        canExport,
        handleDownload,
    } = useBlobField();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    // padding = -minX (since the base viewBox is "-pad -pad w h")
    const padding = -base.x;

    return (
        <div className="panel-viewer blob-field-viewer">
            <div className="path-tool-preview">
                {/* Zoom controls */}
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                {/* Zoomable / pannable SVG */}
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            style={{ touchAction: "none" }}
                            {...panHandlers}
                        >
                            {/* White background */}
                            <rect
                                x={-padding}
                                y={-padding}
                                width={canvasW + padding * 2}
                                height={canvasH + padding * 2}
                                fill="white"
                            />

                            {/* Contour fills – rendered outermost (level 0) to innermost */}
                            <g className="contours">
                                {contourData.map(({ paths, level }) => {
                                    const fillOpacity =
                                        (level + 1) / contourLevels;
                                    return paths.map((d, i) => (
                                        <path
                                            key={`c${level}-${i}`}
                                            d={d}
                                            fill={fillColor}
                                            fillOpacity={fillOpacity}
                                            stroke={strokeColor}
                                            strokeWidth={0.5}
                                            strokeOpacity={0.6}
                                        />
                                    ));
                                })}
                            </g>

                            {/* Grid point dots */}
                            {showGridPoints && (
                                <g className="grid-points">
                                    {gridPoints.map((pt, i) => (
                                        <circle
                                            key={i}
                                            cx={pt.x}
                                            cy={pt.y}
                                            r={pt.weight > 0 ? 3 : 1.5}
                                            fill={strokeColor}
                                            opacity={pt.weight > 0 ? 1 : 0.3}
                                        />
                                    ))}
                                </g>
                            )}
                        </svg>
                    </div>
                </div>

                {/* Footer */}
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {contourData.reduce((n, l) => n + l.paths.length, 0)}{" "}
                        contour path
                        {contourData.reduce((n, l) => n + l.paths.length, 0) !==
                        1
                            ? "s"
                            : ""}{" "}
                        across {contourLevels} level
                        {contourLevels !== 1 ? "s" : ""}.
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
